import { useEffect, useState } from "react";
import { Loader2, Ban } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { money, shortDate } from "@/lib/format";
import { Table, THead, TBody, TR, TH, TD } from "@/components/ui-kit/Table";
import { Button } from "@/components/ui-kit/Button";
import { Dialog, DialogContent, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui-kit/Dialog";
import { PaymentStatusPill } from "./PaymentStatusPill";
import { useFunder } from "@/funder/FunderContext";

/** Per-matter payment history with a void action (kept, never deleted). */
export function PaymentHistory({ matterId, version, onChanged }) {
  const { payerLabel } = useFunder();
  const [rows, setRows] = useState(null);
  const [target, setTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = () => api.get(`/matters/${matterId}/payments`).then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, [matterId, version]);

  const doVoid = async () => {
    setBusy(true); setError("");
    try {
      await api.post(`/payments/${target.id}/void`);
      setTarget(null);
      load();
      onChanged?.();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg border border-border bg-surface shadow-card" data-testid="payment-history">
      <div className="border-b border-border px-5 py-3"><h3 className="text-sm font-semibold text-foreground">Payment history</h3></div>
      <div className="p-2">
        {rows === null ? (
          <div className="grid place-items-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
        ) : rows.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">No payments recorded yet.</p>
        ) : (
          <Table>
            <THead><TR><TH>Date</TH><TH>Payer</TH><TH className="text-right">Amount</TH><TH>Method</TH><TH>Reference</TH><TH>Status</TH><TH /></TR></THead>
            <TBody>
              {rows.map((p) => (
                <TR key={p.id} data-testid={`payment-row-${p.id}`}>
                  <TD className="text-muted-foreground">{shortDate(p.date)}</TD>
                  <TD className="text-muted-foreground">{payerLabel(p.payer)}</TD>
                  <TD className="nums text-right font-medium text-foreground">{money(p.amount)}</TD>
                  <TD className="text-muted-foreground">{p.method}</TD>
                  <TD className="text-muted-foreground">{p.reference || "—"}</TD>
                  <TD><PaymentStatusPill status={p.status} /></TD>
                  <TD className="text-right">
                    {p.status !== "Void" && (
                      <Button size="sm" variant="ghost" onClick={() => setTarget(p)} data-testid={`void-payment-${p.id}`}>
                        <Ban className="h-3.5 w-3.5" /> Void
                      </Button>
                    )}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </div>

      <Dialog open={!!target} onOpenChange={(o) => { if (!busy && !o) setTarget(null); }}>
        <DialogContent data-testid="void-payment-dialog">
          <DialogTitle className="flex items-center gap-2">
            <span className="grid h-8 w-8 place-items-center rounded-md bg-status-void/10 text-status-void"><Ban className="h-4 w-4" /></span>
            Void this payment?
          </DialogTitle>
          <DialogDescription>
            {target && `Voiding ${money(target.amount)} (${target.method}) reverses its balance effect and recomputes invoice status. The record is kept and an audit event is written. This cannot be undone.`}
          </DialogDescription>
          {error && <p className="mt-3 text-sm text-destructive">{error}</p>}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setTarget(null)} disabled={busy} data-testid="void-payment-cancel">Cancel</Button>
            <Button onClick={doVoid} disabled={busy} data-testid="void-payment-confirm">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Ban className="h-4 w-4" />} Void payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
